import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";
import { useRouter, type Route } from "../hooks/useRouter";

export type NavigationGuard = () => Promise<boolean> | boolean;

interface RouterContextValue {
  route: Route;
  navigate: (route: Route) => void;
  /**
   * Register a guard consulted before in-app navigation (e.g. unsaved
   * settings). Return false from the guard to stay. Pass null to clear.
   */
  setNavigationGuard: (guard: NavigationGuard | null) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const guardRef = useRef<NavigationGuard | null>(null);

  const setNavigationGuard = useCallback((guard: NavigationGuard | null) => {
    guardRef.current = guard;
  }, []);

  const navigate = useCallback(
    async (next: Route) => {
      const guard = guardRef.current;
      if (guard && !(await guard())) return;
      router.navigate(next);
    },
    [router.navigate],
  );

  const value = useMemo(
    () => ({ route: router.route, navigate, setNavigationGuard }),
    [router.route, navigate, setNavigationGuard],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRoute() {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error("useRoute must be used within RouterProvider");
  return ctx;
}
